import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useFocusNext } from "../../../hooks/useFocusNext";
import { useUpdateTemplateExtractionFields } from "../../../graphql/hooks/invoiceInterpretationTemplates/useUpdateTemplateExtractionFields";
import type { CustomHighlight } from "../PdfTemplate";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import styled from "styled-components";
import { theme } from "../../../theme";
import Divider from "../../atoms/Divider";
import Body from "../../atoms/typography/Body";
import IconButton from "../../atoms/buttons/IconButton";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import Checkbox from "../../atoms/inputs/Checkbox";
import { Row } from "react-grid-system";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { ViewOffIcon, ViewOnIcon } from "../../atoms/icons/ViewIcons";
import type {
  InvoiceReaderTemplateFieldExtractionModel,
  PossibleFieldPositionWithValue,
} from "../../../models/ClientInvoiceReaderTemplateModel";
import type { FieldWithUIState } from "../../../models/Templates";
import { useTranslation } from "react-i18next";
import type { Position } from "../../../models/Position";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import type { TemplateTabHandle } from "../../../models/Templates";
import Alert from "../../molecules/Alert";
import { mapTemplateFieldsToInput } from "../../../utils/graphql";
import {
  resolvePositionWithValue,
  checkIfFieldHighlighted,
  toggleHighlightField as toggleFieldHighlight,
  syncFieldHighlights as syncFieldHighlightsHelper,
  getHighlightPageNumber,
  buildFieldOptionsWithFallback,
  applyResizeToFields,
  applyFieldClick,
  buildFieldsWithPageNumbers,
  hasDuplicatePositions,
} from "../../../utils/templateHelpers";
import { useGetTextAtPosition } from "../../../graphql/hooks/invoiceInterpretationTemplates/useGetTextAtPosition";
import AutocompleteSelect from "../../atoms/inputs/AutocompleteSelect";
import ToggleAllTextButton from "../../atoms/buttons/ToggleAllTextButton";
import Small from "../../atoms/typography/Small";
import ActiveFieldWrapper from "./ActiveFieldWrapper";

interface Props {
  possibleFields: Array<PossibleFieldPositionWithValue>;
  highlightedFields: Array<CustomHighlight>;
  setHighlightedFields: (field: CustomHighlight[]) => void;
  mandatoryFields: Array<InvoiceReaderTemplateFieldExtractionModel>;
  onSaveAndContinue: () => void;
  onDirtyChange?: () => void;
  onNavigateToPage?: (page: number) => void;
  totalPages?: number;
  onSaveError?: (message: string) => void;
}

const FlexedDivCentered = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const MANDATORY_FILED_BORDER = "rgba(233, 3, 58, 0.8)";
const MANDATORY_FIELD_BACKGROUND = "rgba(233, 3, 58, 0.15)";

const EditMandatoryFields = forwardRef<TemplateTabHandle, Props>(
  (
    {
      possibleFields,
      highlightedFields,
      setHighlightedFields,
      mandatoryFields,
      onSaveAndContinue,
      onDirtyChange,
      onNavigateToPage,
      totalPages,
      onSaveError,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { id: clientIdentifier, templateId } = useParams();
    const { getTextAtPosition } = useGetTextAtPosition();
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const navigateAfterSaveRef = useRef(false);
    const initialFields = useMemo(
      () => buildFieldsWithPageNumbers(mandatoryFields, possibleFields),
      [mandatoryFields, possibleFields],
    );
    const [fields, setFields] = useState<FieldWithUIState[]>(initialFields);
    const [error, setError] = useState<string | null>(null);
    const [duplicateWarnings, setDuplicateWarnings] = useState(new Set<string>());
    const [activeFieldKey, setActiveFieldKey] = useState<string | null>(null);
    const activeFieldKeyRef = useRef<string | null>(null);
    activeFieldKeyRef.current = activeFieldKey;
    const { formRef, lastFocusedInputRef, focusNextInput } = useFocusNext();

    const updateWarning = (key: string, value: string | undefined) => {
      setDuplicateWarnings((prev) => {
        const next = new Set(prev);
        if (value && hasDuplicatePositions(value, possibleFields)) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    };
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;
    const highlightedFieldsRef = useRef(highlightedFields);
    highlightedFieldsRef.current = highlightedFields;

    const markDirty = (isDirty: boolean) => {
      dirtyRef.current = isDirty;
      setDirty(isDirty);
      onDirtyChange?.();
    };

    const { updateTemplateExtractionFields, loading } =
      useUpdateTemplateExtractionFields({
        onError: (e) => {
          const msg = e?.message || t("errors.somethingWentWrong");
          setError(msg);
          onSaveError?.(msg);
        },
        onCompleted: (result) => {
          if (
            result.invoiceInterpretation?.updateTemplateExtractionFields
              ?.success
          ) {
            if (navigateAfterSaveRef.current) { onSaveAndContinue(); }
          } else {
            setError(t("errors.somethingWentWrong"));
          }
        },
      });

    const onToggleHighlightField = (
      fieldType: string,
      positionLabel?: Position,
      positionValue?: Position,
      pageNumberLabel?: number,
      pageNumberValue?: number,
    ) => {
      toggleFieldHighlight(
        fieldType,
        highlightedFields,
        setHighlightedFields,
        MANDATORY_FILED_BORDER,
        MANDATORY_FIELD_BACKGROUND,
        positionLabel,
        positionValue,
        pageNumberLabel,
        pageNumberValue,
      );
    };

    const syncHighlights = useCallback((fields: FieldWithUIState[]) => {
      syncFieldHighlightsHelper(
        fields,
        highlightedFieldsRef.current,
        setHighlightedFields,
        MANDATORY_FILED_BORDER,
        MANDATORY_FIELD_BACKGROUND,
      );
    }, [setHighlightedFields]);

    useEffect(() => {
      if (!dirtyRef.current) {
        const newFields = buildFieldsWithPageNumbers(mandatoryFields, possibleFields, fieldsRef.current);
        setFields(newFields);
        syncHighlights(newFields);
      }
      // possibleFields is intentionally excluded: always current via the ref inside syncHighlights
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mandatoryFields, syncHighlights]);

    const areAllHighlighted = () => {
      // If no fields have positions, return false
      const fieldsWithPositions = fields.filter(
        (field) => field.label?.position || field.value?.position,
      );
      if (fieldsWithPositions.length === 0) return false;

      // Check if all fields with positions are highlighted
      return fieldsWithPositions.every((field) =>
        checkIfFieldHighlighted(field.fieldType, highlightedFields),
      );
    };

    const toggleAll = () => {
      if (areAllHighlighted()) {
        // Hide all - remove all field highlights
        const fieldsToRemove = fields.flatMap((field) => [
          `${field.fieldType}LABEL`,
          `${field.fieldType}VALUE`,
        ]);
        setHighlightedFields(
          highlightedFields.filter(
            (h) => h.label && !fieldsToRemove.includes(h.label),
          ),
        );
      } else {
        // Show all - add highlights for all fields
        const newHighlights: CustomHighlight[] = [];
        fields.forEach((field) => {
          const idLabel = `${field.fieldType}LABEL`;
          const idValue = `${field.fieldType}VALUE`;

          if (field.label?.position) {
            newHighlights.push({
              id: idLabel,
              position: field.label.position,
              label: idLabel,
              pageNumber: field.label.pageNumber ?? getHighlightPageNumber(field.label.value, possibleFields),
              color: MANDATORY_FILED_BORDER,
              background: MANDATORY_FIELD_BACKGROUND,
            });
          }
          if (field.value?.position) {
            newHighlights.push({
              id: idValue,
              position: field.value.position,
              label: idValue,
              pageNumber: field.value.pageNumber ?? getHighlightPageNumber(field.value.value, possibleFields),
              color: MANDATORY_FILED_BORDER,
              background: MANDATORY_FIELD_BACKGROUND,
            });
          }
        });

        // Remove existing field highlights and add new ones
        const fieldsToRemove = fields.flatMap((field) => [
          `${field.fieldType}LABEL`,
          `${field.fieldType}VALUE`,
        ]);
        const filtered = highlightedFields.filter(
          (h) => h.label && !fieldsToRemove.includes(h.label),
        );
        setHighlightedFields([...filtered, ...newHighlights]);
      }
    };

    const save = async () => {
      if (!dirtyRef.current) return;
      setError(null);
      const input = mapTemplateFieldsToInput(fields);
      await updateTemplateExtractionFields({
        input: input,
        templateId,
        clientIdentifier: clientIdentifier!,
      });
      markDirty(false);
    };

    /**
     * Allows the parent to:
     * 1. Trigger a save of this tab's data (`save()`)
     * 2. Check if the tab has unsaved changes (`isDirty()`)
     * 3. Mark the tab as dirty (`markDirty()`)
     */
    useImperativeHandle(ref, () => ({
      save,
      isDirty: () => dirtyRef.current,
      markDirty: () => markDirty(true),
      handleFieldClick: (clickedField) => {
        const result = applyFieldClick(clickedField, activeFieldKeyRef.current, fieldsRef.current, highlightedFieldsRef.current, MANDATORY_FILED_BORDER, MANDATORY_FIELD_BACKGROUND);
        if (!result) return;
        setFields(result.newFields);
        markDirty(true);
        setActiveFieldKey(null);
        updateWarning(result.key, result.resolved.value);
        setHighlightedFields(result.newHighlights);
        onNavigateToPage?.(result.resolved.pageNumber);
        focusNextInput();
      },
      handleHighlightResized: (label: string, newPosition: Position, pageNumber: number) => {
        getTextAtPosition({
          templateId: templateId!,
          clientIdentifier: clientIdentifier!,
          pageNumber,
          position: newPosition,
        }).then((result) => {
          const fetched = result?.data?.invoiceInterpretation?.getTextAtPosition;
          if (!fetched?.position) return;

          const resolved = { value: fetched.value, position: fetched.position, pageNumber };
          const newFields = applyResizeToFields(fieldsRef.current, label, resolved);
          if (!newFields) return;
          setFields(newFields);
          markDirty(true);
          syncFieldHighlightsHelper(
            newFields,
            highlightedFieldsRef.current,
            setHighlightedFields,
            MANDATORY_FILED_BORDER,
            MANDATORY_FIELD_BACKGROUND,
          );
        }).catch((e: any) => {
          setError(e?.message || t("errors.somethingWentWrong"));
        });
      },
    }));

    const onClickSaveAndContinue = async () => {
      navigateAfterSaveRef.current = true;
      try {
        await save();
      } catch (e: any) {
        setError(e.message || t("errors.somethingWentWrong"));
      } finally {
        navigateAfterSaveRef.current = false;
      }
    };

    return (
      <div ref={formRef}>
        {error && <Alert type="error" title={error} />}
        {fields && fields.length > 0 && (
          <>
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="end">
              <ToggleAllTextButton
                onClick={toggleAll}
                showHideAll={areAllHighlighted()}
                showAllText={t("templates.showAll")}
                hideAllText={t("templates.hideAll")}
              />
            </Row>
          </>
        )}
        {fields?.map((field, index) => (
          <div key={`${field.fieldType} + ${index}`}>
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="between" align="center">
              <Body>{t(`templates.fieldLabels.${field.fieldType}`)}</Body>
              <ToggleButton
                onToggle={() =>
                  onToggleHighlightField(
                    field.fieldType,
                    field.label?.position,
                    field.value?.position,
                    field.label?.pageNumber ?? getHighlightPageNumber(field.label?.value, possibleFields),
                    field.value?.pageNumber ?? getHighlightPageNumber(field.value?.value, possibleFields),
                  )
                }
                isOn={checkIfFieldHighlighted(
                  field.fieldType,
                  highlightedFields,
                )}
                size="medium"
                iconOn={<ViewOnIcon color={theme.colors.white} />}
                iconOff={<ViewOffIcon color={theme.colors.primary} />}
                tabIndex={-1}
              />
            </Row>
            <HorizontalSpacer spacing={0.5} />

            <FlexedDivCentered style={{ gap: 32, alignItems: "flex-start" }}>
              <div style={{ width: "100%" }}>
                <BodySmall onClick={() => setActiveFieldKey(`${field.fieldType}LABEL`)} style={{ cursor: "pointer" }}>{t("templates.label")}</BodySmall>
                <FlexedDivCentered>
                  <ActiveFieldWrapper $active={activeFieldKey === `${field.fieldType}LABEL`} onClick={() => setActiveFieldKey(`${field.fieldType}LABEL`)} onFocus={(e) => { setActiveFieldKey(`${field.fieldType}LABEL`); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}>
                    <AutocompleteSelect
                      value={field.label?.value || ""}
                      onChange={(value) => {
                        const newFields = [...fields];
                        const resolved = value
                          ? resolvePositionWithValue(value, possibleFields)
                          : undefined;
                        newFields[index] = {
                          ...newFields[index],
                          label: resolved,
                          hasNoLabel: false,
                        };
                        setFields(newFields);
                        markDirty(true);
                        syncHighlights(newFields);
                        updateWarning(`${field.fieldType}LABEL`, resolved?.value);
                        if (resolved) {
                          if (
                            !checkIfFieldHighlighted(
                              field.fieldType,
                              highlightedFields,
                            )
                          ) {
                            onToggleHighlightField(
                              field.fieldType,
                              resolved.position,
                              newFields[index].value?.position,
                              resolved.pageNumber,
                              newFields[index].value?.pageNumber ?? getHighlightPageNumber(newFields[index].value?.value, possibleFields),
                            );
                          }
                          onNavigateToPage?.(resolved.pageNumber);
                        }
                        focusNextInput();
                      }}
                      options={buildFieldOptionsWithFallback(possibleFields, totalPages, t("common.page"), t("templates.selectAField"), field.label?.value)}
                    />
                  </ActiveFieldWrapper>
                  <IconButton
                    icon={<ClearIcon />}
                    tabIndex={-1}
                    onClick={() => {
                      const newFields = [...fields];
                      newFields[index] = {
                        ...newFields[index],
                        label: undefined,
                      };
                      setFields(newFields);
                      markDirty(true);
                      syncHighlights(newFields);
                      updateWarning(`${field.fieldType}LABEL`, undefined);
                    }}
                  />
                </FlexedDivCentered>
                {duplicateWarnings.has(`${field.fieldType}LABEL`) && (
                  <>
                    <HorizontalSpacer spacing={0.25} />
                    <Small color={theme.colors.textSecondary}>{t("templates.duplicateLabelWarning")}</Small>
                  </>
                )}
                <HorizontalSpacer spacing={0.25} />
                <Checkbox
                  small
                  title={t("templates.valueHasNoLabel")}
                  checked={field.hasNoLabel ?? false}
                  onChange={(checked: boolean) => {
                    setFields((prev) => {
                      const newFields = [...prev];
                      newFields[index] = {
                        ...newFields[index],
                        hasNoLabel: checked,
                        label: checked ? undefined : newFields[index].label,
                      };
                      return newFields;
                    });
                    setDirty(true);
                  }}
                />
              </div>
              <div style={{ width: "100%" }}>
                <BodySmall onClick={() => setActiveFieldKey(`${field.fieldType}VALUE`)} style={{ cursor: "pointer" }}>{t("templates.value")}</BodySmall>
                <FlexedDivCentered>
                  <ActiveFieldWrapper $active={activeFieldKey === `${field.fieldType}VALUE`} onClick={() => setActiveFieldKey(`${field.fieldType}VALUE`)} onFocus={(e) => { setActiveFieldKey(`${field.fieldType}VALUE`); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}>
                    <AutocompleteSelect
                      value={field.value?.value || ""}
                      options={buildFieldOptionsWithFallback(possibleFields, totalPages, t("common.page"), t("templates.selectAField"), field.value?.value)}
                      onChange={(value: string) => {
                        const newFields = [...fields];
                        const resolved = value
                          ? resolvePositionWithValue(value, possibleFields)
                          : undefined;
                        newFields[index] = {
                          ...newFields[index],
                          value: resolved,
                        };
                        setFields(newFields);
                        markDirty(true);
                        syncHighlights(newFields);
                        updateWarning(`${field.fieldType}VALUE`, resolved?.value);
                        if (resolved) {
                          if (
                            !checkIfFieldHighlighted(
                              field.fieldType,
                              highlightedFields,
                            )
                          ) {
                            onToggleHighlightField(
                              field.fieldType,
                              newFields[index].label?.position,
                              resolved.position,
                              newFields[index].label?.pageNumber ?? getHighlightPageNumber(newFields[index].label?.value, possibleFields),
                              resolved.pageNumber,
                            );
                          }
                          onNavigateToPage?.(resolved.pageNumber);
                        }
                        focusNextInput();
                      }}
                    />
                  </ActiveFieldWrapper>
                  <IconButton
                    icon={<ClearIcon />}
                    tabIndex={-1}
                    onClick={() => {
                      const newFields = [...fields];
                      newFields[index] = {
                        ...newFields[index],
                        value: undefined,
                      };
                      setFields(newFields);
                      markDirty(true);
                      syncHighlights(newFields);
                      updateWarning(`${field.fieldType}VALUE`, undefined);
                    }}
                  />
                </FlexedDivCentered>
                {duplicateWarnings.has(`${field.fieldType}VALUE`) && (
                  <>
                    <HorizontalSpacer spacing={0.25} />
                    <Small color={theme.colors.textSecondary}>{t("templates.duplicateValueWarning")}</Small>
                  </>
                )}
              </div>
            </FlexedDivCentered>

            <HorizontalSpacer spacing={1} />
            <Divider />
          </div>
        ))}

        <HorizontalSpacer spacing={2} />
        <Row nogutter justify="end">
          <OutlinedButton
            title={t("random.saveAndContinue")}
            iconRight={<ChevronSmallRightIcon color={theme.colors.primary} />}
            color={theme.colors.primary}
            medium
            onClick={onClickSaveAndContinue}
            disabled={loading || !dirty}
          />
        </Row>
      </div>
    );
  },
);

export default EditMandatoryFields;
